/**
 * Centralized Role-Based Navigation Configuration
 *
 * Maps each UserRole to the navigation items visible for that role.
 * Replaces the per-page pattern of defining navigation arrays inline
 * and passing them to the Header component.
 *
 * Usage:
 *   import { getNavigationForRole } from '@/lib/config/navigation';
 *   const navItems = getNavigationForRole(user.role, pathname);
 *   <Header navigation={navItems} ... />
 */

import type { UserRole } from '@/lib/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single navigation entry rendered in the Header and MobileMenu.
 * Compatible with the existing NavItem interface exported from Header.
 */
export interface NavItem {
  label: string;
  href: string;
  active: boolean;
}

/**
 * A logical group of navigation items (used internally to organise the
 * config before flattening into a NavItem[]).
 */
interface NavSection {
  /** Human-readable section name (for documentation; not rendered yet). */
  section: string;
  items: ReadonlyArray<{ label: string; href: string }>;
}

// ---------------------------------------------------------------------------
// Route definitions (single source of truth for href values)
// ---------------------------------------------------------------------------

const ROUTES = {
  // Customer routes
  dashboard: '/dashboard',
  serviceHistory: '/dashboard/history',
  damageReports: '/dashboard/damage-reports',
  invoices: '/dashboard/invoices',
  invoiceAnalytics: '/dashboard/invoices/analytics',
  invoiceArchive: '/dashboard/invoices/archive',
  receipts: '/dashboard/receipts',
  reports: '/dashboard/reports',
  compliance: '/dashboard/compliance',
  notifications: '/dashboard/notifications',
  emergencyDelivery: '/dashboard/emergency-delivery',
  schedule: '/dashboard/schedule',
  contract: '/dashboard/contract',
  issues: '/dashboard/issues',
  tenantUsers: '/dashboard/users',

  // Shared routes (customer and admin)
  bundles: '/dashboard/bundles',
  barcodes: '/dashboard/barcodes',

  // Admin routes
  admin: '/admin',
  adminBilling: '/admin/billing',
  adminTenants: '/admin/tenants',
  adminUsers: '/admin/users',
  adminFleet: '/admin/fleet',
  adminReminders: '/admin/deliveries/reminders',
  adminEmergencyDelivery: '/admin/emergency-delivery',
  adminDisruptionAlerts: '/admin/notifications/disruption',
  adminNdpc: '/admin/compliance/ndpc',
  adminBreaches: '/admin/compliance/breaches',

  // Warehouse routes
  warehouse: '/warehouse',

  // Profile routes (available to all authenticated users)
  profile: '/profile',
  profileNotifications: '/profile/notifications',
  profilePrivacy: '/profile/privacy',
  profileSecurity: '/profile/security',
} as const;

// ---------------------------------------------------------------------------
// Per-role navigation definitions
// ---------------------------------------------------------------------------

/**
 * facility_director
 * Strategic oversight: dashboards, service history, compliance, reports,
 * invoices, schedule management, and contract details.
 */
const FACILITY_DIRECTOR_NAV: NavSection[] = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', href: ROUTES.dashboard },
    ],
  },
  {
    section: 'Operations',
    items: [
      { label: 'Service History', href: ROUTES.serviceHistory },
      { label: 'Schedule', href: ROUTES.schedule },
    ],
  },
  {
    section: 'Compliance & Reports',
    items: [
      { label: 'Compliance', href: ROUTES.compliance },
      { label: 'Reports', href: ROUTES.reports },
    ],
  },
  {
    section: 'Finance',
    items: [
      { label: 'Invoices', href: ROUTES.invoices },
      { label: 'Contract', href: ROUTES.contract },
    ],
  },
];

/**
 * finance_officer
 * Invoice reconciliation and billing: invoices, analytics, archive,
 * receipts, and contract terms.
 */
const FINANCE_OFFICER_NAV: NavSection[] = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', href: ROUTES.dashboard },
    ],
  },
  {
    section: 'Billing',
    items: [
      { label: 'Invoices', href: ROUTES.invoices },
      { label: 'Invoice Analytics', href: ROUTES.invoiceAnalytics },
      { label: 'Invoice Archive', href: ROUTES.invoiceArchive },
      { label: 'Receipts', href: ROUTES.receipts },
    ],
  },
  {
    section: 'Contract',
    items: [
      { label: 'Contract', href: ROUTES.contract },
    ],
  },
];

/**
 * facility_staff
 * Receiving coordination and delivery verification: service history,
 * damage reports, issues, emergency delivery, notifications, and schedule.
 */
const FACILITY_STAFF_NAV: NavSection[] = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', href: ROUTES.dashboard },
    ],
  },
  {
    section: 'Operations',
    items: [
      { label: 'Service History', href: ROUTES.serviceHistory },
      { label: 'Damage Reports', href: ROUTES.damageReports },
      { label: 'Issues', href: ROUTES.issues },
      { label: 'Schedule', href: ROUTES.schedule },
    ],
  },
  {
    section: 'Delivery',
    items: [
      { label: 'Emergency Delivery', href: ROUTES.emergencyDelivery },
    ],
  },
  {
    section: 'Communication',
    items: [
      { label: 'Notifications', href: ROUTES.notifications },
    ],
  },
];

/**
 * tenant_admin
 * User management for their tenant, plus service overview,
 * notifications, and schedule.
 */
const TENANT_ADMIN_NAV: NavSection[] = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', href: ROUTES.dashboard },
    ],
  },
  {
    section: 'Administration',
    items: [
      { label: 'Users', href: ROUTES.tenantUsers },
    ],
  },
  {
    section: 'Operations',
    items: [
      { label: 'Service History', href: ROUTES.serviceHistory },
      { label: 'Schedule', href: ROUTES.schedule },
    ],
  },
  {
    section: 'Communication',
    items: [
      { label: 'Notifications', href: ROUTES.notifications },
    ],
  },
];

/**
 * operations_manager
 * CleanYarn leadership with cross-tenant access: billing, tenants, users,
 * fleet, bundles, barcodes, reminders, emergency delivery, disruption
 * alerts, and compliance (NDPC + Breaches).
 */
const OPERATIONS_MANAGER_NAV: NavSection[] = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', href: ROUTES.admin },
    ],
  },
  {
    section: 'Business',
    items: [
      { label: 'Billing', href: ROUTES.adminBilling },
      { label: 'Tenants', href: ROUTES.adminTenants },
      { label: 'Users', href: ROUTES.adminUsers },
    ],
  },
  {
    section: 'Logistics',
    items: [
      { label: 'Fleet', href: ROUTES.adminFleet },
      { label: 'Bundles', href: ROUTES.bundles },
      { label: 'Barcodes', href: ROUTES.barcodes },
    ],
  },
  {
    section: 'Scheduling',
    items: [
      { label: 'Reminders', href: ROUTES.adminReminders },
      { label: 'Emergency Delivery', href: ROUTES.adminEmergencyDelivery },
      { label: 'Disruption Alerts', href: ROUTES.adminDisruptionAlerts },
    ],
  },
  {
    section: 'Compliance',
    items: [
      { label: 'NDPC', href: ROUTES.adminNdpc },
      { label: 'Breaches', href: ROUTES.adminBreaches },
    ],
  },
];

/**
 * system_admin
 * Full system access. Currently identical to operations_manager; the
 * structure allows future system-admin-only items to be appended here.
 */
const SYSTEM_ADMIN_NAV: NavSection[] = [
  ...OPERATIONS_MANAGER_NAV,
];

/**
 * warehouse_operations
 * Quality control and inspection only.
 */
const WAREHOUSE_OPERATIONS_NAV: NavSection[] = [
  {
    section: 'Inspections',
    items: [
      { label: 'Inspections', href: ROUTES.warehouse },
    ],
  },
];

/**
 * field_staff
 * Not used in the web app (field staff are redirected to the mobile app).
 * Returns an empty navigation set.
 */
const FIELD_STAFF_NAV: NavSection[] = [];

// ---------------------------------------------------------------------------
// Role -> Navigation map
// ---------------------------------------------------------------------------

const ROLE_NAVIGATION_MAP: Record<UserRole, NavSection[]> = {
  // Customer roles
  facility_director: FACILITY_DIRECTOR_NAV,
  finance_officer: FINANCE_OFFICER_NAV,
  facility_staff: FACILITY_STAFF_NAV,
  tenant_admin: TENANT_ADMIN_NAV,

  // CleanYarn roles
  operations_manager: OPERATIONS_MANAGER_NAV,
  system_admin: SYSTEM_ADMIN_NAV,
  warehouse_operations: WAREHOUSE_OPERATIONS_NAV,
  field_staff: FIELD_STAFF_NAV,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determine whether `currentPath` should mark `href` as active.
 *
 * Rules:
 *  - Exact match is always active.
 *  - For root pages (/dashboard, /admin, /warehouse) only exact matches
 *    count so that nested pages do not highlight the root link.
 *  - For deeper routes, the current path must start with the href to
 *    support nested sub-pages (e.g. /dashboard/invoices/analytics
 *    activates the /dashboard/invoices link).
 */
function isActive(href: string, currentPath: string): boolean {
  if (currentPath === href) {
    return true;
  }

  // Root-level paths should only match exactly
  const rootPaths = [ROUTES.dashboard, ROUTES.admin, ROUTES.warehouse];
  if (rootPaths.includes(href as typeof rootPaths[number])) {
    return false;
  }

  // For everything else, match on prefix (handles sub-pages)
  return currentPath.startsWith(href + '/') || currentPath.startsWith(href + '?');
}

/**
 * Flatten NavSection[] into a flat NavItem[] with `active` computed
 * from the current path.
 */
function flattenSections(
  sections: NavSection[],
  currentPath: string,
): NavItem[] {
  return sections.flatMap((section) =>
    section.items.map((item) => ({
      label: item.label,
      href: item.href,
      active: isActive(item.href, currentPath),
    })),
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Return the navigation items for a given user role, with the `active`
 * flag set based on the current path.
 *
 * @param role        - The authenticated user's primary role.
 * @param currentPath - The current URL pathname (e.g. from useLocation().pathname).
 * @returns An array of NavItem objects ready to pass to <Header />.
 *
 * @example
 * ```ts
 * import { getNavigationForRole } from '@/lib/config/navigation';
 *
 * const pathname = useLocation().pathname;
 * const navItems = getNavigationForRole(user.role, pathname);
 * <Header navigation={navItems} />
 * ```
 */
export function getNavigationForRole(
  role: string,
  currentPath: string,
): NavItem[] {
  const sections = ROLE_NAVIGATION_MAP[role as UserRole];

  if (!sections) {
    // Unknown role: return empty navigation (safe fallback)
    return [];
  }

  return flattenSections(sections, currentPath);
}

/**
 * Return the sectioned navigation for a given user role.
 * Useful when rendering navigation with visual group separators.
 *
 * @param role        - The authenticated user's primary role.
 * @param currentPath - The current URL pathname.
 * @returns An array of { section, items } groups with `active` computed.
 */
export function getNavigationSectionsForRole(
  role: string,
  currentPath: string,
): Array<{ section: string; items: NavItem[] }> {
  const sections = ROLE_NAVIGATION_MAP[role as UserRole];

  if (!sections) {
    return [];
  }

  return sections.map((s) => ({
    section: s.section,
    items: s.items.map((item) => ({
      label: item.label,
      href: item.href,
      active: isActive(item.href, currentPath),
    })),
  }));
}

/**
 * Get the default landing page for a given role.
 * Useful for post-login redirects.
 */
export function getDefaultRouteForRole(role: string): string {
  switch (role as UserRole) {
    case 'facility_director':
    case 'finance_officer':
    case 'facility_staff':
    case 'tenant_admin':
      return ROUTES.dashboard;

    case 'operations_manager':
    case 'system_admin':
      return ROUTES.admin;

    case 'warehouse_operations':
      return ROUTES.warehouse;

    case 'field_staff':
      // Field staff should be redirected to the mobile app.
      // Return a sentinel path that the caller can intercept.
      return '/mobile-redirect';

    default:
      return '/';
  }
}

/**
 * Re-export ROUTES so pages can reference canonical paths without
 * hard-coding strings.
 */
export { ROUTES };
